import React, { useState } from "react";
import Button from "../ui/Button";
import ColorInput from "../ui/ColorInput";

interface ColorScheme {
	name: string;
	background: string;
	text: string;
	border: string;
	iconColor: string;
	iconBackground: string;
	hoverColor: string;
	hoverText: string;
}

interface ColorSchemeModalProps {
	onApply: (scheme: ColorScheme) => void;
	onClose: () => void;
	currentColors: Partial<ColorScheme>;
}

// Predefined color schemes
const predefinedSchemes: ColorScheme[] = [
	{
		name: "Light",
		background: "#ffffff",
		text: "#333333",
		border: "#e5e7eb",
		iconColor: "#4b5563",
		iconBackground: "transparent",
		hoverColor: "#f3f4f6",
		hoverText: "#2563eb",
	},
	{
		name: "Dark",
		background: "#121212",
		text: "#f3f4f6",
		border: "#374151",
		iconColor: "#e5e7eb",
		iconBackground: "#374151",
		hoverColor: "#1f2937",
		hoverText: "#60a5fa",
	},
	{
		name: "Blue",
		background: "#f0f7ff",
		text: "#1e3a8a",
		border: "#bfdbfe",
		iconColor: "#2563eb",
		iconBackground: "#dbeafe",
		hoverColor: "#bfdbfe",
		hoverText: "#1e40af",
	},
	{
		name: "Teal",
		background: "#f0fdfa",
		text: "#134e4a",
		border: "#99f6e4",
		iconColor: "#0d9488",
		iconBackground: "#ccfbf1",
		hoverColor: "#99f6e4",
		hoverText: "#0f766e",
	},
	{
		name: "Rose",
		background: "#fff1f2",
		text: "#9f1239",
		border: "#fecdd3",
		iconColor: "#e11d48",
		iconBackground: "#ffe4e6",
		hoverColor: "#fecdd3",
		hoverText: "#be123c",
	},
	{
		name: "Amber",
		background: "#fffbeb",
		text: "#854d0e",
		border: "#fde68a",
		iconColor: "#d97706",
		iconBackground: "#fef3c7",
		hoverColor: "#fde68a",
		hoverText: "#b45309",
	},
	{
		name: "Purple",
		background: "#faf5ff",
		text: "#6b21a8",
		border: "#e9d5ff",
		iconColor: "#9333ea",
		iconBackground: "#f3e8ff",
		hoverColor: "#e9d5ff",
		hoverText: "#7e22ce",
	},
	{
		name: "Glass",
		background: "rgba(255, 255, 255, 0.8)",
		text: "#1f2937",
		border: "rgba(255, 255, 255, 0.2)",
		iconColor: "#4b5563",
		iconBackground: "rgba(255, 255, 255, 0.3)",
		hoverColor: "rgba(255, 255, 255, 0.4)",
		hoverText: "#2563eb",
	},
];

const ColorSchemeModal: React.FC<ColorSchemeModalProps> = ({
	onApply,
	onClose,
	currentColors,
}) => {
	// Initialize with current colors or defaults
	const [colorScheme, setColorScheme] = useState<ColorScheme>({
		name: "Custom",
		background: currentColors.background || "#ffffff",
		text: currentColors.text || "#333333",
		border: currentColors.border || "#e5e7eb",
		iconColor: currentColors.iconColor || "#4b5563",
		iconBackground: currentColors.iconBackground || "transparent",
		hoverColor: currentColors.hoverColor || "#f3f4f6",
		hoverText: currentColors.hoverText || "#2563eb",
	});

	// Apply a predefined scheme
	const applyPredefinedScheme = (scheme: ColorScheme) => {
		setColorScheme(scheme);
	};

	// Handle individual color changes
	const handleColorChange = (key: keyof ColorScheme, value: string) => {
		setColorScheme((prev) => ({
			...prev,
			[key]: value,
			name: "Custom", // When making changes, it becomes a custom scheme
		}));
	};

	// Preview style for the color scheme cards
	const getPreviewStyle = (scheme: ColorScheme): React.CSSProperties => {
		return {
			background: scheme.background,
			color: scheme.text,
			border: `1px solid ${scheme.border}`,
			padding: "8px",
			borderRadius: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: "8px",
			width: "100%",
			height: "60px",
			cursor: "pointer",
		};
	};

	// Icon preview
	const IconPreview = ({ scheme }: { scheme: ColorScheme }) => (
		<div
			style={{
				background: scheme.iconBackground,
				color: scheme.iconColor,
				padding: "8px",
				borderRadius: "4px",
			}}>
			<svg
				width="16"
				height="16"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round">
				<circle cx="11" cy="11" r="8"></circle>
				<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
			</svg>
		</div>
	);

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">
				<div className="flex justify-between items-center mb-4">
					<h2 className="text-xl font-bold">Color Scheme</h2>
					<button
						className="p-1 rounded-full hover:bg-gray-100"
						onClick={onClose}>
						❌
					</button>
				</div>

				{/* Predefined schemes */}
				<div className="mb-6">
					<h3 className="text-lg font-medium mb-3">
						Predefined Color Schemes
					</h3>
					<div className="grid grid-cols-4 gap-4">
						{predefinedSchemes.map((scheme) => (
							<div
								key={scheme.name}
								onClick={() => applyPredefinedScheme(scheme)}
								className="border rounded overflow-hidden cursor-pointer hover:shadow-md transition-shadow">
								<div style={getPreviewStyle(scheme)}>
									<IconPreview scheme={scheme} />
									<span>{scheme.name}</span>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Custom scheme */}
				<div className="mb-6">
					<h3 className="text-lg font-medium mb-3">Custom Colors</h3>
					<div className="grid grid-cols-2 gap-4">
						<ColorInput
							label="Background Color"
							value={colorScheme.background}
							onChange={(val) =>
								handleColorChange("background", val)
							}
							withOpacity={true}
						/>

						<ColorInput
							label="Text Color"
							value={colorScheme.text}
							onChange={(val) => handleColorChange("text", val)}
						/>

						<ColorInput
							label="Border Color"
							value={colorScheme.border}
							onChange={(val) => handleColorChange("border", val)}
							withOpacity={true}
						/>

						<ColorInput
							label="Icon Color"
							value={colorScheme.iconColor}
							onChange={(val) =>
								handleColorChange("iconColor", val)
							}
						/>

						<ColorInput
							label="Icon Background"
							value={colorScheme.iconBackground}
							onChange={(val) =>
								handleColorChange("iconBackground", val)
							}
							withOpacity={true}
						/>

						<ColorInput
							label="Hover Color"
							value={colorScheme.hoverColor}
							onChange={(val) =>
								handleColorChange("hoverColor", val)
							}
							withOpacity={true}
						/>

						<ColorInput
							label="Hover Text"
							value={colorScheme.hoverText}
							onChange={(val) =>
								handleColorChange("hoverText", val)
							}
						/>
					</div>
				</div>

				{/* Preview */}
				<div className="mb-6">
					<h3 className="text-lg font-medium mb-3">Preview</h3>
					<div
						style={getPreviewStyle(colorScheme)}
						className="h-20 rounded">
						<IconPreview scheme={colorScheme} />
						<span>Logo</span>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								background: colorScheme.background,
								border: `1px solid ${colorScheme.border}`,
								borderRadius: "4px",
								padding: "4px 8px",
								marginLeft: "12px",
							}}>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke={colorScheme.iconColor}
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round">
								<circle cx="11" cy="11" r="8"></circle>
								<line
									x1="21"
									y1="21"
									x2="16.65"
									y2="16.65"></line>
							</svg>
							<span
								style={{
									marginLeft: "4px",
									color: colorScheme.text,
								}}>
								Search
							</span>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								background: colorScheme.iconBackground,
								color: colorScheme.iconColor,
								borderRadius: "9999px",
								width: "32px",
								height: "32px",
								marginLeft: "12px",
							}}>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round">
								<circle cx="9" cy="21" r="1"></circle>
								<circle cx="20" cy="21" r="1"></circle>
								<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
							</svg>
						</div>
					</div>
				</div>

				<div className="flex justify-end pt-4 border-t">
					<Button
						variant="outline"
						onClick={onClose}
						className="mr-2">
						Cancel
					</Button>

					<Button
						variant="primary"
						onClick={() => onApply(colorScheme)}>
						Apply Scheme
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ColorSchemeModal;
